import { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  ResponsiveContainer,
} from 'recharts';
import { toNumber } from '@/lib/number';
import { Card } from '@/components/ui/Card';
import type { CostBreakdown, CategoryBreakdown } from '@/types/financials';

interface CostBreakdownChartProps {
  breakdown: CostBreakdown;
}

const CATEGORY_COLORS: Record<string, string> = {
  feed: '#3b82f6',       // blue-500
  probiotics: '#14b8a6', // teal-500
  chemicals: '#a855f7',  // purple-500
  labor: '#f97316',      // orange-500
  energy: '#eab308',     // yellow-500
  seed: '#ec4899',       // pink-500
  equipment: '#6366f1',  // indigo-500
  transport: '#06b6d4',  // cyan-500
  other: '#9ca3af',      // gray-400
};

function getCategoryColor(category: string): string {
  return CATEGORY_COLORS[category.toLowerCase()] || '#9ca3af';
}

function formatPHP(value: number): string {
  return value.toLocaleString('en-PH', { style: 'currency', currency: 'PHP' });
}

interface ChartDataItem {
  name: string;
  category: string;
  amount: number;
  percentage: number;
  transactions: number;
  source: string;
  color: string;
}

export function CostBreakdownChart({ breakdown }: CostBreakdownChartProps) {
  const totalCost = toNumber(breakdown.total_cost);

  const chartData: ChartDataItem[] = useMemo(() => {
    return breakdown.by_category
      .map((item: CategoryBreakdown) => ({
        name: item.category_name,
        category: item.category,
        amount: toNumber(item.amount),
        percentage: item.percentage,
        transactions: item.transactions,
        source: item.source,
        color: getCategoryColor(item.category),
      }))
      .sort((a, b) => b.amount - a.amount);
  }, [breakdown.by_category]);

  if (chartData.length === 0) {
    return null;
  }

  return (
    <Card>
      <Card.Header>
        <div className="flex items-center justify-between w-full">
          <span>Cost Breakdown</span>
          <span className="text-sm font-normal text-gray-500">
            Total: {formatPHP(totalCost)}
          </span>
        </div>
      </Card.Header>
      <Card.Body>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false} />
              <XAxis
                type="number"
                tickFormatter={(val: number) =>
                  val >= 1000 ? `${(val / 1000).toFixed(0)}K` : String(val)
                }
                stroke="#9ca3af"
                fontSize={12}
              />
              <YAxis
                type="category"
                dataKey="name"
                width={120}
                stroke="#9ca3af"
                fontSize={12}
                tick={{ fill: '#374151' }}
              />
              <Tooltip
                content={<CustomTooltip />}
                cursor={{ fill: 'rgba(0,0,0,0.04)' }}
              />
              <Bar dataKey="amount" radius={[0, 4, 4, 0]} maxBarSize={28}>
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Legend / detail rows below chart */}
        <div className="mt-4 space-y-2 border-t border-gray-100 pt-4">
          {chartData.map((item) => (
            <div
              key={`${item.category}-${item.source}`}
              className="flex items-center justify-between text-sm"
            >
              <div className="flex items-center gap-2">
                <span
                  className="h-3 w-3 rounded-sm shrink-0"
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-gray-700">{item.name}</span>
                {item.source !== 'manual' && (
                  <span className="text-xs text-gray-400">({item.source})</span>
                )}
              </div>
              <div className="flex items-center gap-4">
                <span className="text-gray-500">
                  {item.transactions} txn{item.transactions !== 1 ? 's' : ''}
                </span>
                <span className="text-gray-500">{item.percentage.toFixed(1)}%</span>
                <span className="font-medium text-gray-900 tabular-nums w-28 text-right">
                  {formatPHP(item.amount)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </Card.Body>
    </Card>
  );
}

// Custom tooltip for the chart
function CustomTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload: ChartDataItem }>;
}) {
  if (!active || !payload || payload.length === 0) return null;

  const data = payload[0].payload;

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-medium text-gray-900">{data.name}</p>
      <div className="mt-1 space-y-0.5 text-gray-600">
        <p>Amount: {formatPHP(data.amount)}</p>
        <p>Share: {data.percentage.toFixed(1)}%</p>
        <p>Transactions: {data.transactions}</p>
        <p className="text-gray-400 capitalize">Source: {data.source}</p>
      </div>
    </div>
  );
}
